import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ExternalCompanyCode } from '@/store/server/external/_constant';
import type { ConnectToExternalResponse } from '@/store/server/external/auth/auth.dto';

// ============================================================================
// Types
// ============================================================================

export type ExternalConnectionStatus =
  | 'idle' // 초기 상태 (연동 전)
  | 'connecting' // 연동 진행 중
  | 'connected' // 연동 완료
  | 'refreshing' // 토큰 갱신 중
  | 'error' // 에러 발생
  | 'disconnected'; // 연동 해제됨

export type ExternalAuthErrorCode =
  | 'TOKEN_EXPIRED'
  | 'TOKEN_INVALID'
  | 'REFRESH_EXPIRED'
  | 'REFRESH_FAILED'
  | 'CONNECTION_FAILED'
  | 'NETWORK_ERROR';

export interface ExternalAuthError {
  code: ExternalAuthErrorCode;
  message: string;
  timestamp: number;
}

export interface ExternalTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
}

export interface ExternalConnectionState {
  status: ExternalConnectionStatus;
  tokens: ExternalTokens | null;
  connectedAt: number | null;
  lastRefreshAt: number | null;
  error: ExternalAuthError | null;
}

export interface ExternalAuthState {
  connections: Partial<Record<ExternalCompanyCode, ExternalConnectionState>>;
  activeCompany: ExternalCompanyCode | null;
}

// ============================================================================
// Initial State
// ============================================================================

const createInitialConnectionState = (): ExternalConnectionState => ({
  status: 'idle',
  tokens: null,
  connectedAt: null,
  lastRefreshAt: null,
  error: null,
});

const initialState: ExternalAuthState = {
  connections: {},
  activeCompany: null,
};

// ============================================================================
// Slice
// ============================================================================

const externalAuthSlice = createSlice({
  name: 'externalAuth',
  initialState,
  reducers: {
    // 활성 회사 설정
    setActiveCompany: (state, action: PayloadAction<ExternalCompanyCode | null>) => {
      state.activeCompany = action.payload;

      // 해당 회사의 connection state가 없으면 초기화
      if (action.payload && !state.connections[action.payload]) {
        state.connections[action.payload] = createInitialConnectionState();
      }
    },

    // 연동 시작
    initConnection: (state, action: PayloadAction<ExternalCompanyCode>) => {
      const companyCode = action.payload;
      if (!state.connections[companyCode]) {
        state.connections[companyCode] = createInitialConnectionState();
      }
      state.connections[companyCode]!.status = 'connecting';
      state.connections[companyCode]!.error = null;
    },

    // 연동 성공
    connectionSuccess: (
      state,
      action: PayloadAction<{
        companyCode: ExternalCompanyCode;
        tokens: ConnectToExternalResponse;
      }>,
    ) => {
      const { companyCode, tokens } = action.payload;
      if (!state.connections[companyCode]) {
        state.connections[companyCode] = createInitialConnectionState();
      }
      state.connections[companyCode]!.status = 'connected';
      state.connections[companyCode]!.tokens = tokens;
      state.connections[companyCode]!.connectedAt = Date.now();
      state.connections[companyCode]!.error = null;
    },

    // 연동 실패
    connectionFailed: (
      state,
      action: PayloadAction<{
        companyCode: ExternalCompanyCode;
        error: ExternalAuthError;
      }>,
    ) => {
      const { companyCode, error } = action.payload;
      if (!state.connections[companyCode]) {
        state.connections[companyCode] = createInitialConnectionState();
      }
      state.connections[companyCode]!.status = 'error';
      state.connections[companyCode]!.error = error;
    },

    // 토큰 갱신 시작
    startRefresh: (state, action: PayloadAction<ExternalCompanyCode>) => {
      const companyCode = action.payload;
      if (state.connections[companyCode]) {
        state.connections[companyCode]!.status = 'refreshing';
      }
    },

    // 토큰 갱신 성공
    refreshSuccess: (
      state,
      action: PayloadAction<{
        companyCode: ExternalCompanyCode;
        tokens: ConnectToExternalResponse;
      }>,
    ) => {
      const { companyCode, tokens } = action.payload;
      if (state.connections[companyCode]) {
        state.connections[companyCode]!.status = 'connected';
        state.connections[companyCode]!.tokens = tokens;
        state.connections[companyCode]!.lastRefreshAt = Date.now();
        state.connections[companyCode]!.error = null;
      }
    },

    // 토큰 갱신 실패
    refreshFailed: (
      state,
      action: PayloadAction<{
        companyCode: ExternalCompanyCode;
        error: ExternalAuthError;
      }>,
    ) => {
      const { companyCode, error } = action.payload;
      if (state.connections[companyCode]) {
        state.connections[companyCode]!.status = 'error';
        state.connections[companyCode]!.error = error;
        // 갱신 실패 시 토큰 제거
        state.connections[companyCode]!.tokens = null;
      }
    },

    // 연동 해제
    disconnect: (state, action: PayloadAction<ExternalCompanyCode>) => {
      const companyCode = action.payload;
      if (state.connections[companyCode]) {
        state.connections[companyCode]!.status = 'disconnected';
        state.connections[companyCode]!.tokens = null;
        state.connections[companyCode]!.error = null;
      }
      // 활성 회사가 해당 회사면 초기화
      if (state.activeCompany === companyCode) {
        state.activeCompany = null;
      }
    },

    // 에러 초기화
    clearError: (state, action: PayloadAction<ExternalCompanyCode>) => {
      const companyCode = action.payload;
      if (state.connections[companyCode]) {
        state.connections[companyCode]!.error = null;
      }
    },

    // 전체 상태 초기화 (로그아웃 등)
    resetExternalAuth: () => initialState,

    // localStorage에서 hydration (앱 초기화 시)
    hydrateFromStorage: (
      state,
      action: PayloadAction<{
        activeCompany: ExternalCompanyCode | null;
        connections: Partial<Record<ExternalCompanyCode, { tokens: ExternalTokens; connectedAt: number }>>;
      }>,
    ) => {
      const { activeCompany, connections } = action.payload;
      state.activeCompany = activeCompany;

      // 저장된 토큰 정보로 connection 상태 복원
      Object.entries(connections).forEach(([code, data]) => {
        const companyCode = code as ExternalCompanyCode;
        if (data && data.tokens) {
          state.connections[companyCode] = {
            status: 'connected',
            tokens: data.tokens,
            connectedAt: data.connectedAt,
            lastRefreshAt: null,
            error: null,
          };
        }
      });
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const externalAuthActions = externalAuthSlice.actions;
export default externalAuthSlice;
